import styled from "styled-components";

interface TypographyProps {
  fontColor?: string;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
}

interface SurfaceProps {
  backgroundColor?: string;
}

interface NavigationModeProps {
  forceMobile?: boolean;
  backgroundColor?: string;
  desktopCollapsed?: boolean;
  mobileRailVisible?: boolean;
  collapsed?: boolean;
}

interface NavButtonProps {
  active?: boolean;
  fontColor?: string;
  fontFamily?: string;
}

interface MobileNavListProps {
  rail?: boolean;
  open?: boolean;
}

interface MobileIconButtonProps {
  active?: boolean;
  fontColor?: string;
}

interface ToggleButtonProps extends TypographyProps {
  open?: boolean;
  collapsed?: boolean;
}

export const ViewerShell = styled.div<SurfaceProps>`
  width: 100%;
  background: ${({ backgroundColor }) => backgroundColor || "#f8fafc"};
`;

export const Hero = styled.div<SurfaceProps>`
  padding: 32px;
  background: linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.06),
      rgba(15, 23, 42, 0.02)
    ),
    ${({ backgroundColor }) => backgroundColor || "#ffffff"};
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
`;

export const HeroTitle = styled.h1<TypographyProps>`
  margin: 0;
  color: ${({ fontColor }) => fontColor || "#0f172a"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: ${({ fontSize }) => fontSize || "28px"};
  font-weight: ${({ fontWeight }) => fontWeight || "700"};
  line-height: 1.15;
`;

export const HeroMeta = styled.p<TypographyProps>`
  margin: 12px 0 0;
  color: ${({ fontColor }) => fontColor || "#475569"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: ${({ fontSize }) => fontSize || "14px"};
  font-weight: ${({ fontWeight }) => fontWeight || "400"};
  line-height: 1.5;
  opacity: 0.85;
`;

export const ViewerLayout = styled.div<NavigationModeProps>`
  display: grid;
  grid-template-columns: ${({ forceMobile, mobileRailVisible, desktopCollapsed }) => {
    if (forceMobile) {
      return mobileRailVisible ? "58px minmax(0, 1fr)" : "minmax(0, 1fr)";
    }

    return desktopCollapsed
      ? "72px minmax(0, 1fr)"
      : "minmax(260px, 320px) minmax(0, 1fr)";
  }};
  gap: ${({ forceMobile, mobileRailVisible }) =>
    forceMobile ? (mobileRailVisible ? "16px" : "0") : "24px"};
  padding: ${({ forceMobile }) => (forceMobile ? "16px" : "24px")};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: ${({ forceMobile, mobileRailVisible }) =>
      forceMobile && mobileRailVisible
        ? "58px minmax(0, 1fr)"
        : "minmax(0, 1fr)"};
    gap: ${({ forceMobile, mobileRailVisible }) =>
      forceMobile ? (mobileRailVisible ? "16px" : "0") : "16px"};
    padding: 16px;
  }
`;

export const StickyNav = styled.nav<NavigationModeProps>`
  position: sticky;
  top: 88px;
  align-self: flex-start;
  display: ${({ forceMobile }) => (forceMobile ? "none" : "flex")};
  flex-direction: column;
  gap: 10px;
  padding: ${({ collapsed }) => (collapsed ? "12px 10px" : "16px")};
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: ${({ backgroundColor }) =>
    backgroundColor || "rgba(255, 255, 255, 0.9)"};
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

export const DesktopNavToggle = styled.button<ToggleButtonProps>`
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 12px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px")};
  display: inline-flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
  gap: 10px;
  background: #f8fafc;
  color: ${({ fontColor }) => fontColor || "#334155"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }
`;

export const NavButton = styled.button<NavButtonProps>`
  width: 100%;
  border: none;
  border-radius: 14px;
  padding: 12px 14px;
  text-align: left;
  background: ${({ active }) => (active ? "#0f172a" : "transparent")};
  color: ${({ active, fontColor }) => (active ? "#ffffff" : fontColor || "#334155")};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;

  &:hover {
    background: ${({ active }) => (active ? "#0f172a" : "#e2e8f0")};
  }
`;

export const MobileNav = styled.div<NavigationModeProps>`
  display: ${({ forceMobile }) => (forceMobile ? "block" : "none")};
  position: sticky;
  top: 72px;
  z-index: 6;
  align-self: flex-start;
  ${({ forceMobile, backgroundColor }) =>
    forceMobile
      ? `
        width: 58px;
        padding: 8px;
        overflow: visible;
        border-radius: 14px;
        border: 1px solid rgba(148, 163, 184, 0.22);
        background: ${backgroundColor || "rgba(255, 255, 255, 0.97)"};
        backdrop-filter: blur(10px);
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
      `
      : `
        width: min(260px, 100%);
        padding: 0;
        border: none;
        background: transparent;
        box-shadow: none;
      `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

export const MobileNavList = styled.div<MobileNavListProps>`
  ${({ rail, open }) =>
    rail
      ? `
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
      `
      : `
        width: min(220px, 80vw);
        margin-top: 8px;
        max-height: ${open ? "min(52vh, 420px)" : "0"};
        overflow-y: auto;
        padding: ${open ? "10px" : "0 10px"};
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        box-shadow: ${open ? "0 12px 28px rgba(15, 23, 42, 0.12)" : "none"};
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        transform: ${open ? "translateY(0)" : "translateY(-8px)"};
        opacity: ${open ? "1" : "0"};
        pointer-events: ${open ? "auto" : "none"};
        transition: max-height 0.2s ease, padding 0.2s ease, transform 0.2s ease,
          opacity 0.2s ease, box-shadow 0.2s ease;
      `}
  gap: 8px;
`;

export const MobileNavToggle = styled.button<ToggleButtonProps>`
  width: 100%;
  border: none;
  border-radius: 12px;
  background: ${({ open }) => (open ? "#e2e8f0" : "#f8fafc")};
  padding: 10px 12px;
  color: ${({ fontColor }) => fontColor || "#0f172a"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

export const MobileRailToggleRow = styled.div`
  display: flex;
  padding: 16px 16px 0;
`;

export const MobileRailToggleButton = styled.button`
  display: inline-flex;
  min-width: 96px;
  height: 42px;
  padding: 0 16px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 999px;
  border: 1px solid rgba(99, 102, 241, 0.28);
  background: rgba(255, 255, 255, 0.96);
  color: #4f46e5;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

export const MobileIconButton = styled.button<MobileIconButtonProps>`
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ active }) => (active ? "#0f172a" : "#f1f5f9")};
  color: ${({ active, fontColor }) => (active ? "#ffffff" : fontColor || "#334155")};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ active }) => (active ? "#0f172a" : "#e2e8f0")};
  }
`;

export const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const SectionCard = styled.section<SurfaceProps>`
  scroll-margin-top: 100px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: ${({ backgroundColor }) => backgroundColor || "#ffffff"};
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-radius: 18px;
    padding: 18px;
  }
`;

export const DividerCard = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, transparent, #94a3b8, transparent);
  margin: 8px 0;
`;

export const SectionHeading = styled.h2<TypographyProps>`
  margin: 0 0 14px;
  color: ${({ fontColor }) => fontColor || "#0f172a"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: ${({ fontSize }) => fontSize || "22px"};
  font-weight: ${({ fontWeight }) => fontWeight || "700"};
  line-height: 1.2;
`;

export const RichContent = styled.div<TypographyProps>`
  color: ${({ fontColor }) => fontColor || "#334155"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: ${({ fontSize }) => fontSize || "15px"};
  font-weight: ${({ fontWeight }) => fontWeight || "400"};
  line-height: 1.7;
  overflow-wrap: anywhere;

  p,
  ul,
  ol,
  blockquote {
    margin: 0 0 1rem;
  }

  p:last-of-type,
  ul:last-of-type,
  ol:last-of-type,
  blockquote:last-of-type {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1.25rem 0 0.75rem;
    line-height: 1.25;
  }

  a {
    color: #1d4ed8;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 14px;
  }
`;

export const PlainText = styled.p<TypographyProps>`
  margin: 0;
  color: ${({ fontColor }) => fontColor || "#334155"};
  font-family: ${({ fontFamily }) => fontFamily || "Poppins, sans-serif"};
  font-size: ${({ fontSize }) => fontSize || "15px"};
  font-weight: ${({ fontWeight }) => fontWeight || "400"};
  line-height: 1.7;
  white-space: pre-line;
`;

export const MediaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

export const MediaImage = styled.img`
  width: 100%;
  max-height: 520px;
  object-fit: contain;
  border-radius: 16px;
  background: #f8fafc;
`;

export const VideoFrame = styled.iframe`
  width: 100%;
  min-height: 360px;
  border: none;
  border-radius: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 220px;
  }
`;

export const VideoPlayer = styled.video`
  width: 100%;
  border-radius: 16px;
`;

export const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

export const LinkCard = styled.a`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #f8fafc;
  color: #0f172a;
  text-decoration: none;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(37, 99, 235, 0.35);
  }
`;

export const LinkTitle = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 700;
`;

export const LinkUrl = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  word-break: break-all;
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
`;
